import { EyeOpenIcon, FileTextIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IClinicianForm,
  IWardSurveyOverview,
} from "../../../@types/admin/AdminSurvey";
import { TableSorted } from "../../../components/TableSorted";
import { useFileDownload } from "../../../hooks/mutations/admin/fileTransfer";
import {
  useClinicianForms,
  useWardSurveys,
} from "../../../hooks/queries/admin/surveys";
import { useModal } from "../../../hooks/useModal";
import { surveyExportRequests } from "../../../services/admin/surveysService";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  Tab,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  TabsContainer,
  useMockPagination,
} from "../shared";
import {
  RowActions,
  SurveyNameTag,
  SurveyNameTags,
} from "./AdminSurveys.styles";
import { ClinicianFormReportModal } from "./ClinicianFormReportModal";
import { WardSurveyDrillDownModal } from "./WardSurveyDrillDownModal";

type ISurveyTab = "forms" | "proms";

/**
 * How many survey names a ward row draws before it collapses the rest into a
 * "+N more" pill. A busy ward carries fifty; drawing them all made one row six
 * lines tall and the table unreadable (EN-5146 UI feedback). Open ward lists
 * every one of them.
 */
const VISIBLE_SURVEY_NAMES = 4;

/**
 * Surveys & PROMs admin page (EN-5164), migrated from the Retool Surveys app
 * — specifically `moduleSurveysProms`, which holds every query and modal the
 * ticket describes.
 *
 * ⚠️ `page_Surveys` also embeds a second module, `moduleSurveys`, which was
 * never exported (it is a bare `<Module>` stub with no content and no props).
 * Nothing in this page depends on it: every endpoint the ticket lists is in
 * `moduleSurveysProms`. What it contributes is unknown and still needs a
 * Retool export to settle — see `portal/retool-migration.md`.
 *
 * Retool stacked the two modules on one screen. They are separate concerns —
 * a catalogue of clinician forms, and per-ward PROMs completion — so they are
 * tabs here rather than one long scroll.
 */
export function AdminSurveys() {
  document.title = "Surveys - Admin";

  const { openModal, closeModal } = useModal();
  const { downloadFile, isDownloading } = useFileDownload();
  const [tab, setTab] = useState<ISurveyTab>("forms");

  const {
    clinicianForms,
    isLoading: formsLoading,
    isError: formsError,
  } = useClinicianForms();
  const {
    wardSurveys,
    isLoading: promsLoading,
    isError: promsError,
  } = useWardSurveys();

  const formsPagination = useMockPagination({
    data: clinicianForms,
    searchFields: ["name"],
  });
  const promsPagination = useMockPagination({
    data: wardSurveys,
    searchFields: ["hospitalName", "wardName"],
  });

  const formColumns: ColumnDef<IClinicianForm, string>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Form", enableSorting: true },
      { accessorKey: "id", header: "ID", enableSorting: true },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Generate report"
              icon={<FileTextIcon />}
              disabled={!row.original.id}
              onClick={() => {
                openModal(
                  <ClinicianFormReportModal
                    form={row.original}
                    onClose={closeModal}
                  />,
                  { width: "440px", showCloseButton: true }
                );
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  const promColumns: ColumnDef<IWardSurveyOverview, string>[] = useMemo(
    () => [
      {
        accessorKey: "hospitalName",
        header: "Hospital",
        enableSorting: true,
      },
      { accessorKey: "wardName", header: "Ward", enableSorting: true },
      {
        id: "wardId",
        header: "Ward ID",
        accessorFn: (row) => String(row.wardId ?? ""),
        enableSorting: true,
      },
      {
        id: "surveyCount",
        header: "Surveys",
        accessorFn: (row) => String((row.surveyNames ?? []).length),
        enableSorting: true,
      },
      {
        id: "surveyNames",
        header: "Assigned",
        // The full list still feeds the search even though only the first few
        // are drawn.
        accessorFn: (row) => (row.surveyNames ?? []).join(", "),
        cell: ({ row }) => {
          const names = row.original.surveyNames ?? [];
          const overflow = names.length - VISIBLE_SURVEY_NAMES;
          return (
            <SurveyNameTags>
              {names.slice(0, VISIBLE_SURVEY_NAMES).map((name) => (
                <SurveyNameTag key={name}>{name}</SurveyNameTag>
              ))}
              {overflow > 0 && (
                <SurveyNameTag
                  more
                  title={names.slice(VISIBLE_SURVEY_NAMES).join("\n")}
                >
                  +{overflow} more
                </SurveyNameTag>
              )}
            </SurveyNameTags>
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Open ward"
              icon={<EyeOpenIcon />}
              onClick={() => {
                openModal(<WardSurveyDrillDownModal ward={row.original} />, {
                  width: "min(1200px, 96vw)",
                  maxHeight: "90vh",
                  showCloseButton: true,
                });
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  const isFormsTab = tab === "forms";
  const pagination = isFormsTab ? formsPagination : promsPagination;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Surveys &amp; PROMs</PageTitle>
      </PageHeader>

      <TabsContainer>
        <Tab active={isFormsTab} onClick={() => setTab("forms")}>
          Clinician forms
        </Tab>
        <Tab active={!isFormsTab} onClick={() => setTab("proms")}>
          Hospital PROMs
        </Tab>
      </TabsContainer>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder={
            isFormsTab
              ? "Search by form name..."
              : "Search by hospital or ward..."
          }
          value={pagination.searchTerm}
          onChange={(e) => pagination.setSearchTerm(e.target.value)}
        />

        {/*
          Not a create action, but it is this page's one page-level action, so
          it sits in the slot every other page's action sits in rather than
          floating beside the title.
        */}
        {!isFormsTab && (
          <FiltersActions>
            <FilterButton
              variant="primary"
              disabled={isDownloading}
              onClick={() =>
                downloadFile(surveyExportRequests.wardSurveys(), {
                  fallbackFilename: "HospitalSurveys.xlsx",
                })
              }
            >
              {isDownloading ? "Exporting..." : "Export all wards"}
            </FilterButton>
          </FiltersActions>
        )}
      </FiltersContainer>

      {isFormsTab && formsError && (
        <ErrorMessage>
          Failed to load the clinician forms. Try refreshing the page.
        </ErrorMessage>
      )}
      {!isFormsTab && promsError && (
        <ErrorMessage>
          Failed to load the ward PROMs. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper
          refreshKeys={[["admin-clinician-forms"], ["admin-ward-surveys"]]}
        >
          {isFormsTab ? (
            <TableSorted
              columns={formColumns}
              data={formsPagination.items}
              emptyStateText={
                formsLoading ? <TableLoadingState /> : "No rows found"
              }
            />
          ) : (
            <TableSorted
              columns={promColumns}
              data={promsPagination.items}
              emptyStateText={
                promsLoading ? <TableLoadingState /> : "No rows found"
              }
            />
          )}
        </TableWrapper>
        {isFormsTab ? (
          <TablePagination
            pagination={formsPagination.pagination}
            onPageChange={formsPagination.setPage}
            onPageSizeChange={formsPagination.setPageSize}
            exportCsv={{
              filename: "clinician-forms",
              columns: formColumns,
              rows: formsPagination.allItems,
            }}
          />
        ) : (
          <TablePagination
            pagination={promsPagination.pagination}
            onPageChange={promsPagination.setPage}
            onPageSizeChange={promsPagination.setPageSize}
            exportCsv={{
              filename: "ward-proms",
              columns: promColumns,
              rows: promsPagination.allItems,
            }}
          />
        )}
      </TableContainer>
    </PageContainer>
  );
}
